import { useState, useRef, useCallback } from "react";
import { T } from "../theme";

const THRESHOLD = 80; // px to pull before triggering refresh

export default function PullToRefresh({ onRefresh, children }) {
  const [pulling, setPulling] = useState(false);
  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const startY = useRef(0);
  const scrollRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    // Only enable pull if scrolled to top
    if (scrollRef.current && scrollRef.current.scrollTop > 0) return;
    startY.current = e.touches[0].clientY;
    setPulling(true);
  }, []);

  const handleTouchMove = useCallback(
    (e) => {
      if (!pulling || refreshing) return;
      const delta = e.touches[0].clientY - startY.current;
      if (delta < 0) {
        setPullY(0);
        return;
      }
      // Dampen the pull (feels more natural)
      setPullY(Math.min(delta * 0.5, 120));
    },
    [pulling, refreshing]
  );

  const handleTouchEnd = useCallback(async () => {
    if (!pulling) return;
    if (pullY >= THRESHOLD && !refreshing) {
      setRefreshing(true);
      setPullY(THRESHOLD * 0.5);
      try {
        await onRefresh();
      } catch (e) {
        console.error("Refresh failed:", e);
      }
      setRefreshing(false);
    }
    setPulling(false);
    setPullY(0);
  }, [pulling, pullY, refreshing, onRefresh]);

  const progress = Math.min(pullY / THRESHOLD, 1);

  return (
    <div
      style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}
    >
      {/* Pull indicator */}
      <div
        style={{
          height: pullY,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          flexShrink: 0,
          transition: pulling ? "none" : "height 0.2s ease",
        }}
      >
        {pullY > 10 && (
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              border: `2.5px solid ${T.border}`,
              borderTopColor: progress >= 1 ? T.orange : T.textDim,
              animation: refreshing ? "spin 0.6s linear infinite" : "none",
              transform: `rotate(${progress * 360}deg)`,
              transition: refreshing ? "none" : "transform 0.1s",
            }}
          />
        )}
      </div>

      {/* Scrollable content */}
      <div
        ref={scrollRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ flex: 1, overflowY: "auto" }}
      >
        {children}
      </div>
    </div>
  );
}
